import { useState, useRef, useCallback, useEffect } from 'react';
import { WS_URL, getToken } from '../config';

/**
 * Quiz WebSocket — Self-Paced Race
 *
 * States: idle → lobby → countdown → playing → finished
 *
 * In "playing", each player progresses through questions independently.
 * All questions are received upfront in game_starting.
 */

const QUIZ_WS_URL = `${WS_URL}/ws/quiz`;

const initialState = {
  phase: 'idle',
  roomCode: null,
  playerId: null,
  players: [],
  isHost: false,
  // Game data
  gameType: 'text',
  questions: [],
  currentIndex: 0,
  timeLimit: 90,
  // Per-question feedback
  lastResult: null, // { correct, correctId }
  totalCorrect: 0,
  // Other players' progress
  playerProgress: {}, // playerId -> { name, current, correct, total, finished, finishMs }
  // Host-only: questionId -> correctId, populated from `host_keys` after start.
  hostKeys: {},
  // Host-only: per-student per-question answer events. Shape:
  //   { [playerId]: { [questionId]: { answerId, correctId, correct, name } } }
  // Built from the `answer_event` stream; students never receive this.
  hostAnswers: {},
  // Countdown
  countdown: 3,
  // Penalty
  penaltyMs: 0,
  // Final
  finalScores: [],
  error: null,
};

export function useQuizWebSocket() {
  const [state, setState] = useState(initialState);
  const wsRef = useRef(null);
  const countdownRef = useRef(null);

  const send = useCallback((type, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const token = getToken();
    const wsUrl = `${QUIZ_WS_URL}${token ? `?token=${token}` : ''}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setState(prev => ({ ...prev, error: null }));
    };

    ws.onmessage = (event) => {
      const { type, payload } = JSON.parse(event.data);

      switch (type) {
        case 'room_created':
          setState(prev => ({
            ...prev,
            phase: 'lobby',
            roomCode: payload.roomCode,
            playerId: payload.playerId,
            isHost: true,
            error: null,
          }));
          break;

        case 'room_joined':
          setState(prev => ({
            ...prev,
            phase: 'lobby',
            roomCode: payload.roomCode,
            playerId: payload.playerId,
            error: null,
          }));
          break;

        case 'player_joined':
          setState(prev => {
            const newPhase = (prev.phase === 'idle' || prev.phase === 'finished') ? 'lobby' : prev.phase;
            return {
              ...prev,
              phase: newPhase,
              players: payload.players,
              // Reset game state when returning to lobby (rematch)
              ...(prev.phase === 'finished' ? {
                questions: [],
                currentIndex: 0,
                totalCorrect: 0,
                lastResult: null,
                penaltyMs: 0,
                playerProgress: {},
                finalScores: [],
              } : {}),
            };
          });
          break;

        case 'player_left':
          setState(prev => ({
            ...prev,
            players: payload.players,
          }));
          break;

        case 'game_starting': {
          const countdownStart = payload.countdown || 3;
          setState(prev => ({
            ...prev,
            phase: 'countdown',
            countdown: countdownStart,
            gameType: payload.gameType || 'text',
            questions: payload.questions,
            timeLimit: payload.timeLimit,
            currentIndex: 0,
            totalCorrect: 0,
            lastResult: null,
            penaltyMs: 0,
            playerProgress: {},
            hostKeys: {},
            hostAnswers: {},
            finalScores: [],
          }));
          // Tick countdown 3→2→1→go
          if (countdownRef.current) clearInterval(countdownRef.current);
          let remaining = countdownStart;
          countdownRef.current = setInterval(() => {
            remaining--;
            if (remaining <= 0) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
              setState(prev => prev.phase === 'countdown' ? { ...prev, phase: 'playing', countdown: 0 } : prev);
            } else {
              setState(prev => prev.phase === 'countdown' ? { ...prev, countdown: remaining } : prev);
            }
          }, 1000);
          break;
        }

        case 'answer_result':
          setState(prev => ({
            ...prev,
            lastResult: {
              questionId: payload.questionId,
              correct: payload.correct,
              correctId: payload.correctId,
            },
            totalCorrect: payload.totalCorrect,
            penaltyMs: payload.penaltyMs || prev.penaltyMs,
          }));
          break;

        case 'progress_update':
          setState(prev => ({
            ...prev,
            playerProgress: {
              ...prev.playerProgress,
              [payload.playerId]: {
                name: payload.playerName,
                current: payload.current,
                correct: payload.correct,
                total: payload.total,
                finished: false,
              },
            },
          }));
          break;

        case 'player_finished':
          setState(prev => ({
            ...prev,
            playerProgress: {
              ...prev.playerProgress,
              [payload.playerId]: {
                name: payload.playerName,
                current: payload.total,
                correct: payload.correct,
                total: payload.total,
                finished: true,
                finishMs: payload.finishMs,
              },
            },
          }));
          break;

        case 'game_over':
          setState(prev => ({
            ...prev,
            phase: 'finished',
            finalScores: payload.finalScores,
          }));
          break;

        case 'host_keys':
          setState(prev => ({
            ...prev,
            hostKeys: payload.keys || {},
          }));
          break;

        case 'answer_event':
          setState(prev => {
            const playerEntry = prev.hostAnswers[payload.playerId] || { name: payload.playerName, byQuestion: {} };
            return {
              ...prev,
              hostAnswers: {
                ...prev.hostAnswers,
                [payload.playerId]: {
                  name: payload.playerName,
                  byQuestion: {
                    ...playerEntry.byQuestion,
                    [payload.questionId]: {
                      answerId: payload.answerId,
                      correctId: payload.correctId,
                      correct: payload.correct,
                    },
                  },
                },
              },
            };
          });
          break;

        case 'error':
          setState(prev => ({ ...prev, error: payload.message }));
          break;

        default:
          break;
      }
    };

    ws.onerror = () => {
      setState(prev => ({ ...prev, error: 'Connection lost' }));
    };
  }, []);

  const disconnect = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setState(initialState);
  }, []);

  // Advance to next question locally (client-driven)
  const advanceQuestion = useCallback(() => {
    setState(prev => {
      const next = prev.currentIndex + 1;
      if (next >= prev.questions.length) {
        // Player has answered all questions — stay in playing, wait for game_over
        return { ...prev, currentIndex: next, lastResult: null };
      }
      return { ...prev, currentIndex: next, lastResult: null };
    });
  }, []);

  // Submit answer and immediately advance
  const submitAnswer = useCallback((questionId, answerId) => {
    send('submit_answer', { questionId, answerId });
  }, [send]);

  const createRoom = useCallback((playerName, numQuestions, timeLimit, gameType, topics) => {
    connect();
    const checkAndSend = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        send('create_room', {
          playerName,
          numQuestions,
          timeLimit,
          gameType,
          topics: topics && topics.length > 0 ? topics : undefined,
        });
      } else {
        setTimeout(checkAndSend, 50);
      }
    };
    checkAndSend();
  }, [connect, send]);

  const joinRoom = useCallback((playerName, roomCode) => {
    connect();
    const checkAndSend = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        send('join_room', { playerName, roomCode: roomCode.toUpperCase() });
      } else {
        setTimeout(checkAndSend, 50);
      }
    };
    checkAndSend();
  }, [connect, send]);

  const startGame = useCallback(() => {
    send('start_game', {});
  }, [send]);

  const rematch = useCallback(() => {
    send('rematch', {});
  }, [send]);

  const leaveRoom = useCallback(() => {
    send('leave_room', {});
    disconnect();
  }, [send, disconnect]);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return {
    ...state,
    createRoom,
    joinRoom,
    startGame,
    submitAnswer,
    advanceQuestion,
    rematch,
    leaveRoom,
    disconnect,
  };
}
